import { useEffect, useRef, useState } from 'react';
import {
  useAuthSession,
  useOpenTemplateDraft,
  useStoreWebsiteEditorStateV3,
  WebsiteTemplateServiceError,
  type StoreWebsiteEditorStateV3,
} from '@dukanti/shared';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';

export type StorefrontEditorData =
  | { readonly status: 'loading' }
  | {
      readonly status: 'error';
      readonly retry: () => void;
      readonly runtimeUnavailable?: boolean;
      readonly storeId?: string | null;
    }
  | {
      readonly status: 'ready';
      readonly storeId: string;
      readonly storeSlug: string;
      readonly state: StoreWebsiteEditorStateV3;
      readonly reload: () => Promise<unknown>;
    };

/**
 * What the storefront screens need before they can show anything: the store
 * of the signed-in merchant, its slug (the canvas document is addressed by
 * it) and the editor state of ONE template (epic #228 P3). `templateId` comes
 * from the edit route; `null` is the entry read, which names the template the
 * store opened last. A failure of either is one error with one Retry.
 */
export function useStorefrontEditorData(templateId: string | null): StorefrontEditorData {
  const authSession = useAuthSession(supabase);
  const store = useCurrentStore(authSession);
  const editor = useStoreWebsiteEditorStateV3(supabase, store.storeId, templateId);

  // Background failures must not unmount a valid editor or silently drop region input.
  if (store.isError || (editor.isError && !editor.data)) {
    return {
      status: 'error',
      runtimeUnavailable:
        editor.error instanceof WebsiteTemplateServiceError &&
        editor.error.code === 'template_unavailable',
      storeId: store.storeId,
      retry: () => {
        if (store.isError) store.refetch();
        if (editor.isError) void editor.refetch();
      },
    };
  }
  if (store.isLoading || editor.isLoading || !editor.data) return { status: 'loading' };
  if (!store.storeId || !store.storeSlug) {
    return { status: 'error', retry: store.refetch };
  }
  return {
    status: 'ready',
    storeId: store.storeId,
    storeSlug: store.storeSlug,
    state: editor.data,
    reload: () => editor.refetch(),
  };
}

function isTemplateUnavailable(error: unknown): boolean {
  return error instanceof WebsiteTemplateServiceError && error.code === 'template_unavailable';
}

/**
 * The edit route's data (epic #228 P3). It reads this template's state first:
 *
 * - **No saved draft** (no head): it is ready with that state and opens
 *   nothing. Visiting an address never starts a design; the route sends the
 *   merchant to the library, where "Use template" (with its question) is the
 *   only way to create a draft.
 * - **A head**: it opens the template's draft on the server
 *   (`open_store_website_template_draft_v3`, idempotent) and is ready only
 *   after the open succeeded. The open's settled refresh re-reads this state,
 *   so every save, locale change, update and publish uses the head the open
 *   returned: a draft or the published row, never an archived row (an archived
 *   head, e.g. the formerly published row of a template another publish
 *   replaced, is copied into a new draft by the open).
 *
 * One open per store and template; Retry after a failed open repeats it with
 * the same mutation id. `template_unavailable` from the read or the open is
 * reported as `runtimeUnavailable`, so the route sends the merchant to the
 * library.
 */
export function useOpenedTemplateEditorData(
  templateId: string,
  newMutationId: () => string = () => crypto.randomUUID(),
): StorefrontEditorData {
  const authSession = useAuthSession(supabase);
  const store = useCurrentStore(authSession);
  const storeId = store.storeId;
  const editor = useStoreWebsiteEditorStateV3(supabase, storeId, templateId);
  // Decided by the first read: a template with no head is never opened from here.
  const hasHead = editor.data === undefined ? null : editor.data.head !== null;
  const open = useOpenTemplateDraft(supabase, storeId ?? '');
  const { mutate } = open;
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef<string | null>(null);
  const mutationRef = useRef<{ readonly target: string; readonly id: string } | null>(null);

  useEffect(() => {
    if (!storeId || hasHead !== true) return;
    const target = `${storeId}:${templateId}`;
    // Strict mode runs effects twice; one open per store, template and attempt.
    const key = `${target}:${attempt}`;
    if (startedRef.current === key) return;
    startedRef.current = key;
    if (mutationRef.current?.target !== target) {
      mutationRef.current = { target, id: newMutationId() };
    }
    mutate({ templateId, clientMutationId: mutationRef.current.id });
  }, [attempt, hasHead, mutate, newMutationId, storeId, templateId]);

  const opened = open.isSuccess && open.variables.templateId === templateId;

  if (store.isError) return { status: 'error', storeId, retry: store.refetch };
  if (open.isError) {
    return {
      status: 'error',
      runtimeUnavailable: isTemplateUnavailable(open.error),
      storeId,
      retry: () => setAttempt((count) => count + 1),
    };
  }
  if (editor.isError && !editor.data) {
    return {
      status: 'error',
      runtimeUnavailable: isTemplateUnavailable(editor.error),
      storeId,
      retry: () => void editor.refetch(),
    };
  }
  if (store.isLoading) return { status: 'loading' };
  if (!storeId || !store.storeSlug) return { status: 'error', retry: store.refetch };
  if (editor.isLoading || !editor.data) return { status: 'loading' };
  if (hasHead === true && !opened) return { status: 'loading' };
  return {
    status: 'ready',
    storeId,
    storeSlug: store.storeSlug,
    state: editor.data,
    reload: () => editor.refetch(),
  };
}
